import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/modules/auth/AuthContext";
import { AuthLoading } from "@/modules/auth/AuthFrame";
import { supabase } from "@/integrations/supabase/client";

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // The pending-invitations nudge only applies to the post-login landing page
  // (/dashboard) — it must never block direct navigation to a workspace the
  // user already belongs to, or to any other page.
  //
  // The check is keyed to location.key rather than cached in plain state:
  // React Router reuses this component instance across route changes (same
  // component type at the same position under <Routes>), so a one-time check
  // would go stale — a leftover "has invites" once trapped users on
  // /invitations after accepting, until a hard refresh.
  const isDashboard = location.pathname === "/dashboard";
  const [inviteCheck, setInviteCheck] = useState<{ key: string; pending: boolean } | null>(null);

  useEffect(() => {
    if (!user?.email || !isDashboard) return;
    let cancelled = false;
    supabase
      .from("workspace_invitations")
      .select("id")
      .eq("email", user.email)
      .eq("status", "pending")
      .limit(1)
      .then(({ data }) => {
        if (!cancelled) {
          setInviteCheck({ key: location.key, pending: (data ?? []).length > 0 });
        }
      });
    return () => { cancelled = true; };
  }, [user?.email, isDashboard, location.key]);

  const checkingInvites = isDashboard && !!user?.email && inviteCheck?.key !== location.key;

  if (loading || checkingInvites) {
    return <AuthLoading />;
  }

  if (!user) {
    const target = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(target)}`} replace />;
  }

  const skipped = sessionStorage.getItem("pendingInvitationsSkipped") === "1";
  if (isDashboard && inviteCheck?.pending && !skipped) {
    return <Navigate to="/invitations" replace state={{ fromLogin: (location.state as any)?.fromLogin }} />;
  }

  return <>{children}</>;
}
